/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/jsx-no-comment-textnodes */
/* eslint-disable jsx-a11y/lang */
import React, { useEffect, useState } from 'react';
import { Header, useGlobal, NavLink, Typography } from '@rcg/ui-react';
import { Button } from '@material-ui/core';
import { navigate } from '@gatsbyjs/reach-router/';
import { useQuery, useReactiveVar, useApolloClient } from '@apollo/client';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { Link } from '@rcg/ui-react/lib/components';
import { useIdle } from 'react-use';
import { globalizationPath } from './utils/globalization/globalizationPath';
import { resolveNavigationTarget } from './utils/globalization/navigationTarget';
import { LAUNCH_DARKLY_FLAGS_AIR } from './operations/queries';
import useLdFlags from './hooks/useLdFlags';
import useMaintenanceStatus from './hooks/useMaintenanceStatus';
import Routes from './Routes';
import Dictionary from './utils/dictionary';
import useDictionary from './hooks/useDictionary';
import getBrand from './utils/getBrand';
import {
  HEADER_OPTIONS_PATHS,
  NAVIGATION_PATHS,
  SESSION_STORAGE_AMEND_ORIGIN,
  SESSION_STORAGE_CURRENT_BOOKING,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  SESSION_STORAGE_BOOKING_TOKEN_NAME,
  FLIGHT_SEARCH_TYPE,
  COOKIE_KEYS,
  APPLICATION_NAME,
  ENABLE_JIRA_WIDGET,
  ENABLE_SIGN_IN_FLAG,
  MAINTENANCE_MODES,
} from './utils/constants';
import { isMaintenanceExemptPath } from './utils/maintenance';
import useStyles from './index.style';
import AirFooter from './components/AirFooter';
import AirLoader from './components/AirLoader';
import PreOutageBanner from './components/PreOutageBanner';
import MaintenanceTakeover from './components/MaintenanceTakeover';
import JiraIssueCollector from './components/Layouts';
import {
  prefixPathVar,
  globalDataVar,
  filterPanelVar,
  isLoggedInVar,
  loyaltyDataVar,
} from './operations/localState/reactiveVariables';
import { ReactComponent as Logo } from './assets/svg/logo.svg';
import { ReactComponent as CelebrityLogo } from './assets/svg/celebrity-cruises-logo-x-white.svg';
import {
  getCookies,
  signOffProfile,
  appendGlobalizationToPath,
  getManageRedirectionUrl,
} from './utils/common';
import royalAppdynamics from './utils/royalAppdynamics';
import celAppdynamics from './utils/celAppdynamics';
import { LDUser } from './utils/ldUser';
import SessionMonitor from './utils/session/session-monitor';

// eslint-disable-next-line consistent-return
const App = () => {
  const {
    loading,
    error,
    data: { market, country, lang, currency, isoCode },
  } = useGlobal();

  const searchParams = new URLSearchParams(window.location.search);

  // params defined for deeplink on browse path
  const shipCode = searchParams.get('shipCode');
  const sailDate = searchParams.get('date');

  // params defined for deeplink with booking reservation
  const bookingId = searchParams.get('bookingId');
  const lastName = searchParams.get('lastName');
  const urlToken = searchParams.get('token');

  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  // Kept for signOut's signOffProfile(client) -> client.clearStore(); the Apollo cache still
  // carries ldFlags and the flag-off fallback data, so sign-out must keep clearing it.
  const client = useApolloClient();
  const [hideMenuOptions, setHideMenuOptions] = useState(true);
  const [profileName, setProfileName] = useState();

  const isLoggedIn = useReactiveVar(isLoggedInVar);
  const filterPanel = useReactiveVar(filterPanelVar);
  const isAmendOrigin = sessionStorage.getItem(SESSION_STORAGE_AMEND_ORIGIN) || false;
  const showHeaderOptions = HEADER_OPTIONS_PATHS.some((path) =>
    window.location.pathname.includes(path),
  );
  const backToAmend = isLoggedIn && isAmendOrigin;
  // Not composed here: globalizationPath resolves marketPath, and duplicating the composition
  // would reintroduce the Celebrity `/us/en` prefix for prefixPathVar's consumers.
  const prefixPath = globalizationPath(market, lang, '');
  const isIdle = useIdle(1.8e6); // 30 minutes track user inactivity
  const isCelebrity = getBrand().id === 'cel';

  const flagsVariables = {
    ldUser: LDUser(),
    language: lang,
    clientApp: APPLICATION_NAME,
    application: APPLICATION_NAME,
    brand: getBrand()?.ldBrand,
  };
  const { data: initialFlags } = useQuery(LAUNCH_DARKLY_FLAGS_AIR, {
    fetchPolicy: 'no-cache',
    variables: flagsVariables,
  });
  // Warms the exact cache entry useEnableFastifyApi reads (cache-first, same variables) and
  // gates the initial render below: pages mounting before the flag resolves would latch the
  // GraphQL branch for their whole mount (useEnableFastifyApi latches per mount), sending every
  // fetch on the landing page through the graph after each full page load. If the flags query
  // errors, loading settles false and the killswitch default (GraphQL) applies — never a hang.
  const { loading: ldFlagsLoading } = useLdFlags();
  const {
    loading: maintenanceLoading,
    mode: maintenanceMode,
    isBannerEnabled: isPreOutageBannerEnabled,
    supportPhoneNumber: maintenancePhoneNumber,
  } = useMaintenanceStatus();

  useEffect(() => {
    if (!showHeaderOptions) {
      setHideMenuOptions(true);
    } else setHideMenuOptions(false);
  }, [showHeaderOptions]);

  const getUserProfileName = (loyaltyData) => {
    const parts = loyaltyData?.split('|');
    const name = parts?.[0].split('=');
    return name?.[1];
  };

  const signOut = async () => {
    signOffProfile(client);
    if (backToAmend) {
      window.location.replace(getManageRedirectionUrl());
    } else {
      window.location.replace(
        `${prefixPath}${NAVIGATION_PATHS.base}${NAVIGATION_PATHS.bookFlights}`,
      );
      setProfileName(null);
      loyaltyDataVar(null);
    }
  };

  const loyaltyData = useReactiveVar(loyaltyDataVar) || getCookies(COOKIE_KEYS.LOYALTY_DATA);

  useEffect(() => {
    const name = loyaltyData && getUserProfileName(loyaltyData);
    setProfileName(name);
  }, [loyaltyData]);

  useEffect(async () => {
    const loadScript = (src) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
      return script;
    };

    loadScript(process.env.REACT_APP_APPDYNAMICS_URL);

    if (isCelebrity) {
      celAppdynamics();
      loadScript(process.env.REACT_APP_TEALIUM_URI_CEL);
    } else {
      royalAppdynamics();
      loadScript(process.env.REACT_APP_TEALIUM_URI);
    }
  }, []);

  useEffect(() => {
    if (isIdle) {
      signOut();
    }
  }, [isIdle]);

  if (loading || ldFlagsLoading || maintenanceLoading) return <AirLoader open />;

  if (error) return <p>Error</p>;
  if (market && lang) {
    prefixPathVar(prefixPath);
    globalDataVar({ country, currency, language: lang });

    const baseUrlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
    const manageUrlPath = `${baseUrlPath}${NAVIGATION_PATHS.manage}`;
    const bookFlightUrlPath = `${baseUrlPath}${NAVIGATION_PATHS.bookFlights}`;
    const paymentUrlPath = `${baseUrlPath}${NAVIGATION_PATHS.payment}`;
    const isBrowseUrl = window.location.pathname.includes(NAVIGATION_PATHS.browse);
    const isPaymentUrl = window.location.pathname.startsWith(paymentUrlPath);
    const isManagePage = window.location.pathname.includes(NAVIGATION_PATHS.manage);
    const isResultsDeparture = window.location.pathname.includes(NAVIGATION_PATHS.resultsDeparture);
    const isResultsPage =
      window.location.pathname.includes(FLIGHT_SEARCH_TYPE.DEPARTURE) ||
      window.location.pathname.includes(FLIGHT_SEARCH_TYPE.RETURN);

    // It returns an array of all existing routes on navigation paths with the globalizacion path market/lang
    const validPaths = [
      baseUrlPath,
      ...Object.values(NAVIGATION_PATHS).map((value) => baseUrlPath + value),
    ];

    const isAValidPath = validPaths.includes(window.location.pathname);
    const accessToken = getCookies('accessToken');
    const isBrowseDeepLink = Boolean(isBrowseUrl && shipCode && sailDate);
    const isBookingDeepLink = Boolean(
      isResultsDeparture && shipCode && sailDate && lastName && bookingId,
    );

    const allowNavigation =
      isBrowseUrl ||
      isManagePage ||
      accessToken ||
      isBrowseDeepLink ||
      isBookingDeepLink ||
      isResultsDeparture ||
      urlToken;

    // To handle app navigation
    if (!isLoggedIn) {
      if (!isAValidPath) {
        const newGlobalPath = resolveNavigationTarget({
          pathname: window.location.pathname,
          market,
          lang,
          allowNavigation,
        });
        if (newGlobalPath) {
          const queryParams = document.location.search;
          navigate(newGlobalPath + (searchParams.size > 0 ? `${queryParams}` : ''), {
            replace: true,
          });
        } else {
          navigate(bookFlightUrlPath, { replace: true });
        }
      }
    } else if (!isAValidPath) {
      navigate(manageUrlPath, { replace: true });
    }

    const isReviewFlight = window.location.pathname.includes('review-flight');
    const rederDefaultFooter = !isPaymentUrl && !isReviewFlight;

    const redirectToLookUp = () => {
      if (backToAmend) {
        sessionStorage.removeItem(SESSION_STORAGE_CURRENT_BOOKING);
        sessionStorage.removeItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
        sessionStorage.removeItem(SESSION_STORAGE_BOOKING_TOKEN_NAME);
      }
    };

    const handleRedirections = () => {
      if (!backToAmend) {
        isLoggedInVar(false);
      }
      redirectToLookUp();
    };

    const isSigninEnabled = initialFlags?.ldFlags[ENABLE_SIGN_IN_FLAG];
    const isAvaiblableJiraWidget = initialFlags?.ldFlags[ENABLE_JIRA_WIDGET];

    const isMaintenanceScope = !isMaintenanceExemptPath(window.location.pathname);
    const isMaintenanceTakeover = isMaintenanceScope && maintenanceMode !== MAINTENANCE_MODES.OFF;
    const isPreOutageBannerVisible =
      isMaintenanceScope && !isMaintenanceTakeover && isPreOutageBannerEnabled;

    const logoHrefUrl = appendGlobalizationToPath('/', { market, lang, isoCode, country });
    return (
      <div className="main-container">
        <Header
          position="sticky"
          elevation={0}
          className={`${classes.headerContainer} ${isCelebrity ? 'cel' : 'rcl'} ${
            isResultsPage ? '' : 'stretch'
          }`}>
          <>
            <a href={logoHrefUrl}>
              {isCelebrity ? <CelebrityLogo /> : <Logo className={classes.logo} />}
            </a>
            {isSigninEnabled && profileName && (
              <div className={classes.signOut}>
                <Link className="greetingLink" href={NAVIGATION_PATHS.guestAccount}>
                  <span className="greetingHeader" data-cs-mask>
                    {profileName &&
                      `${getDictionary(
                        Dictionary.headerBookingAmendGreeting.key,
                      )}, ${profileName} `}
                    <span className="blankSpaceSpan">|</span>
                  </span>
                </Link>
                <Button variant="text" onClick={signOut}>
                  <span className="exit-label">
                    {getDictionary(Dictionary.headerBookingAmendSignOut.key)}
                  </span>
                  <ExitToAppIcon className="exit-icon" />
                </Button>
              </div>
            )}
            {!profileName && !hideMenuOptions && (
              <NavLink
                onClick={handleRedirections}
                component="a"
                icon=""
                href={
                  backToAmend
                    ? getManageRedirectionUrl()
                    : `${prefixPath}${NAVIGATION_PATHS.base}${NAVIGATION_PATHS.bookFlights}`
                }>
                <Typography variant="body2" className="headerTypography">
                  {getDictionary(Dictionary.airHeaderExitReservation.key)}
                </Typography>
              </NavLink>
            )}
          </>
        </Header>
        {isPreOutageBannerVisible && <PreOutageBanner phoneNumber={maintenancePhoneNumber} />}
        {isMaintenanceTakeover ? (
          <MaintenanceTakeover mode={maintenanceMode} phoneNumber={maintenancePhoneNumber} />
        ) : (
          <Routes urlPath={baseUrlPath} />
        )}
        {isAvaiblableJiraWidget && <JiraIssueCollector />}
        {rederDefaultFooter && (
          <AirFooter
            className={`${filterPanel?.isOpen ? classes.footerToRight : ''} ${
              isResultsPage ? '' : classes.stretch
            } `}
          />
        )}
        {!!loyaltyData && <SessionMonitor logOut={signOut} />}
      </div>
    );
  }
};

export default App;
